import { absoluteGradingEnabled } from "../../../../../helpers/scheduleGrading";
import { calculateLaborGradeScore } from "../../schedule_components/ScheduleBusinessScorecard";
import React from "react";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import Tooltip from "@mui/material/Tooltip";

import VisualCustomLineChart from "../../schedule_components/VisualCustomLineChart";
import {
  generateLaborSummary,
  formatPersonName,
  get_t_str,
  get_t_str_minute_precision,
  getCardColor2,
} from "../../../../../helpers/helpers";

const DEFAULT_GRADE_WEIGHTS = {
  grade_intraday_weight: 0.25,
  grade_budget_weight: 0.75,
  grade_budget_under_multiplier: 0.25,
  grade_budget_over_multiplier: 1,
  grade_budget_difficulty_multiplier: 1,
  grade_intraday_difficulty_multiplier: 1,
};

const isNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value);

const getMemberId = (shift) => {
  if (typeof shift.store_team_member === "object") {
    return shift.store_team_member?.id;
  }
  if (shift.store_team_member !== undefined) {
    return shift.store_team_member;
  }
  return shift.store_team_member_id;
};

const getRoleName = (roleId, roleNames) => {
  const roleInfo = roleNames.find((role) => role.id === roleId);
  return roleInfo ? roleInfo.role_name : "";
};

const getRoleColorClass = (roleId, roleNames) =>
  getCardColor2(roleId, roleNames, false, false);

const toNumber = (value) => {
  if (isNumber(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getShiftStart = (shift) => toNumber(shift.shift_start);
const getShiftEnd = (shift) => toNumber(shift.shift_end);

const getShiftLength = (shift) => {
  if (isNumber(shift.shift_length)) {
    return shift.shift_length;
  }
  const start = getShiftStart(shift);
  const end = getShiftEnd(shift);
  if (isNumber(start) && isNumber(end)) {
    return end - start;
  }
  return 0;
};

const getShiftDuration = (shift) => {
  const start = getShiftStart(shift);
  const end = getShiftEnd(shift);
  if (isNumber(start) && isNumber(end)) {
    return end - start;
  }
  return getShiftLength(shift);
};

function ShiftBreakMarkers({ shift, previousShift }) {
  const duration = getShiftDuration(shift);
  if (duration <= 0) return null;
  const breaks = [
    { start: shift.break_start, duration: shift.break_length, label: "Meal break" },
    { start: shift.break_start_2, duration: shift.break_length_2, label: "Second meal break" },
    ...(shift.paid_breaks || []).filter((item) => !item.is_archived).map((item) => ({
      start: item.break_start, duration: item.break_duration, label: "Paid rest break", paid: true,
    })),
  ].filter((item) => Number.isFinite(Number(item.start)) && Number(item.duration) > 0);
  return breaks.map((item, index) => {
    const start = Number(item.start);
    const length = Number(item.duration);
    const previousBreaks = previousShift ? [
      { label: "Meal break", start: previousShift.break_start, duration: previousShift.break_length },
      { label: "Second meal break", start: previousShift.break_start_2, duration: previousShift.break_length_2 },
      ...(previousShift.paid_breaks || []).filter((rest) => !rest.is_archived).map((rest) => ({
        label: "Paid rest break", start: rest.break_start, duration: rest.break_duration,
      })),
    ].filter((rest) => Number(rest.duration) > 0) : [];
    const changed = previousShift && !previousBreaks.some((old) => old.label === item.label
      && Math.abs(Number(old.start) - start) < 1e-8 && Math.abs(Number(old.duration) - length) < 1e-8);
    const label = `${item.label}: ${get_t_str_minute_precision(start)}–${get_t_str_minute_precision(start + length)} (${Math.round(length * 60)} min)${changed ? " · Updated break" : ""}`;
    return (
      <Tooltip key={index} title={label} arrow>
        <span
          aria-label={label}
          className={`absolute top-0 ${changed ? "ring-2 ring-inset ring-violet-300" : ""} ${item.paid ? "h-2.5 rounded-b-sm bg-emerald-200" : "h-8 bg-black/20"}`}
          style={{
            left: `${((start - getShiftStart(shift)) / duration) * 100}%`,
            width: `${(length / duration) * 100}%`,
          }}
        />
      </Tooltip>
    );
  });
}

const parseDateString = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }
  const parts = value.split("-").map(Number);
  if (parts.length < 3 || parts.some(Number.isNaN)) {
    return null;
  }
  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
};

const normalizeDate = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value === "string") {
    const parsed = parseDateString(value);
    if (parsed) {
      return parsed;
    }
    const fallback = new Date(value);
    if (Number.isNaN(fallback.getTime())) {
      return null;
    }
    return new Date(
      fallback.getFullYear(),
      fallback.getMonth(),
      fallback.getDate()
    );
  }
  return null;
};

const getDayIndex = (dateStr, weekStartDate) => {
  const date = normalizeDate(dateStr);
  const weekStart = normalizeDate(weekStartDate);
  if (!date || !weekStart) {
    return null;
  }
  const diffMs = date.getTime() - weekStart.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0 || diffDays > 6) {
    return null;
  }
  return diffDays;
};

const getAvailabilityWindows = (availability) => {
  if (!availability) {
    return [];
  }
  const windows = [];
  const start1 = toNumber(availability.available_from);
  const end1 = toNumber(availability.available_to);
  if (isNumber(start1) && isNumber(end1) && end1 > start1) {
    windows.push([start1, end1]);
  }
  const start2 = toNumber(availability.available_from_2);
  const end2 = toNumber(availability.available_to_2);
  if (isNumber(start2) && isNumber(end2) && end2 > start2) {
    windows.push([start2, end2]);
  }
  return windows;
};

const getUnavailabilityRanges = (availability, startHour, endHour) => {
  if (!availability || startHour >= endHour) {
    return [];
  }
  const windows = getAvailabilityWindows(availability);
  if (!windows.length) {
    return [[startHour, endHour]];
  }

  const clamped = windows
    .map(([start, end]) => [
      Math.max(startHour, start),
      Math.min(endHour, end),
    ])
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0]);

  if (!clamped.length) {
    return [[startHour, endHour]];
  }

  const ranges = [];
  let cursor = startHour;
  clamped.forEach(([start, end]) => {
    if (start > cursor) {
      ranges.push([cursor, start]);
    }
    if (end > cursor) {
      cursor = end;
    }
  });
  if (cursor < endHour) {
    ranges.push([cursor, endHour]);
  }
  return ranges;
};

const buildInitials = (name) => {
  if (!name) {
    return "";
  }
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 1) {
    return parts[0][0].toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const sumColumn = (rows, column) =>
  rows.reduce((total, row) => total + (row[column] || 0), 0);

const aggregateByDateAndQh = (rows) => {
  const aggregated = {};
  rows.forEach((row) => {
    const key = `${row.date}-${row.qh_num}`;
    if (!aggregated[key]) {
      aggregated[key] = {
        date: row.date,
        qh_num: row.qh_num,
        labor: 0,
      };
    }
    aggregated[key].labor += row.labor || 0;
  });
  return Object.values(aggregated);
};

const joinByDateAndQh = (valueRows, bestRows) =>
  bestRows.map((bestRow) => {
    const match = valueRows.find(
      (row) => row.date === bestRow.date && row.qh_num === bestRow.qh_num
    );
    return {
      date: bestRow.date,
      qh_num: bestRow.qh_num,
      valueLabor: match ? match.labor : 0,
      bestLabor: bestRow.labor,
    };
  });

export const calculateScheduleGradeScore = (value, best, gradeWeights) => {
  const config = Array.isArray(gradeWeights) ? gradeWeights[0] : gradeWeights;
  if (absoluteGradingEnabled(config)) {
    return calculateLaborGradeScore({ laborRows: value, targetLaborRows: best, gradeWeights });
  }
  const days = [...new Set(best.map((item) => item.date))];
  const scores = [];
  const weights = { ...DEFAULT_GRADE_WEIGHTS, ...(gradeWeights || {}) };

  days.forEach((day) => {
    const bestRows = best.filter((row) => row.date === day);
    const valueRows = value.filter((row) => row.date === day);
    const bestHours = sumColumn(bestRows, "hours");
    const valueHours = sumColumn(valueRows, "hours");
    if (!bestHours) {
      return;
    }
    // Forecast demand without scheduled labor is a zero, not an ungraded day.
    if (!valueHours) {
      scores.push(0);
      return;
    }

    const valueAggregated = aggregateByDateAndQh(valueRows);
    const bestAggregated = aggregateByDateAndQh(bestRows);
    const joined = joinByDateAndQh(valueAggregated, bestAggregated);

    let sumDifference = 0;
    joined.forEach((row) => {
      const diff = row.valueLabor - row.bestLabor;
      if (diff < 0) {
        sumDifference += diff;
      }
    });

    const bestLaborSum = sumColumn(bestAggregated, "labor");
    let intradayCost = 0;
    if (bestLaborSum > 0) {
      intradayCost =
        (sumDifference * weights.grade_intraday_difficulty_multiplier +
          bestLaborSum) /
        bestLaborSum;
    }

    const laborSum = sumColumn(bestRows, "labor");
    const intradayWeight = laborSum === 0 ? 0 : weights.grade_intraday_weight;
    const budgetWeight = laborSum === 0 ? 1 : weights.grade_budget_weight;

    const budgetMultiplier =
      bestHours > valueHours
        ? weights.grade_budget_under_multiplier
        : weights.grade_budget_over_multiplier;

    const score =
      (intradayCost * intradayWeight +
        (1 -
          (weights.grade_budget_difficulty_multiplier *
            budgetMultiplier *
            Math.abs(bestHours - valueHours)) /
            bestHours) *
          budgetWeight) *
      100;

    scores.push(score);
  });

  if (!scores.length) {
    return 0;
  }
  const total = scores.reduce((sum, score) => sum + score, 0);
  return total / scores.length;
};

export const calcScheduleLetter = (score) => {
  if (score == null) return "-";
  if (score >= 90) {
    return "A";
  }
  if (score >= 80) {
    return "B";
  }
  if (score >= 70) {
    return "C";
  }
  if (score >= 60) {
    return "D";
  }
  if (score === 0) {
    return "-";
  }
  return "F";
};

export const getScheduleGradeColor = (score) => {
  if (score == null) return "gray";
  if (score >= 90) {
    return "#059669";
  }
  if (score >= 80) {
    return "#65a30d";
  }
  if (score >= 70) {
    return "#f5be27";
  }
  if (score >= 60) {
    return "#ea580c";
  }
  if (score === 0) {
    return "gray";
  }
  return "#dc2626";
};

export default function AutoScheduleDailyView(props) {
  const {
    dateStr,
    schedule = [],
    scheduleLabor = [],
    forecastedLabor = [],
    storeTeamMembers = [],
    roleNames = [],
    gradeWeights,
    week_start_date,
    variantLabel,
    showTimeline = true,
    showChart = true,
    compact = false,
    className = "",
    previousSchedule = [],
    changedShifts = [],
    timelineStartHour,
    timelineEndHour,
    timelineCompact = false,
    showTimelineAxis = true,
    embedded = false,
  } = props;

  if (!dateStr) {
    return null;
  }

  const daySchedule = schedule.filter(
    (shift) => shift.date === dateStr && !shift.other_store
  );
  const dayLabor = scheduleLabor.filter(
    (row) => row.date === dateStr && !row.other_store
  );
  const dayForecast = forecastedLabor.filter((row) => row.date === dateStr);

  const isOpenShift = (shift) => {
    if (shift.status === "AVAILABLE") {
      return true;
    }
    const memberId = getMemberId(shift);
    return memberId === null || memberId === undefined || memberId < 0;
  };

  const openShifts = daySchedule.filter(isOpenShift);
  const scheduledShifts = daySchedule.filter((shift) => !isOpenShift(shift));

  const hoursByMember = {};
  daySchedule.forEach((shift) => {
    const memberId = getMemberId(shift);
    if (!memberId || memberId < 0) {
      return;
    }
    hoursByMember[memberId] = (hoursByMember[memberId] || 0) + (shift.hours || 0);
  });

  const memberLookup = {};
  storeTeamMembers.forEach((member) => {
    memberLookup[member.id] = member;
  });

  const groupedByMember = {};
  scheduledShifts.forEach((shift) => {
    const memberId = getMemberId(shift);
    if (!memberId || memberId < 0) {
      return;
    }
    if (!groupedByMember[memberId]) {
      groupedByMember[memberId] = [];
    }
    groupedByMember[memberId].push(shift);
  });

  const memberRows = Object.keys(groupedByMember)
    .map((memberId) => {
      const member = memberLookup[memberId];
      const name = member ? formatPersonName(member) : "Unknown";
      const shifts = groupedByMember[memberId].sort(
        (a, b) => getShiftStart(a) - getShiftStart(b)
      );
      const firstShiftStart = shifts.length ? getShiftStart(shifts[0]) : 0;
      const primaryRoleId = shifts.length
        ? shifts[0].role_id || shifts[0].role_id_id
        : null;
      return {
        id: Number(memberId),
        name,
        hours: hoursByMember[memberId] || 0,
        firstShiftStart: firstShiftStart ?? 0,
        shifts,
        primaryRoleId,
      };
    })
    .sort((a, b) => a.firstShiftStart - b.firstShiftStart);
  const rosterCount = memberRows.length;

  const allShiftTimes = daySchedule.flatMap((shift) => [
    getShiftStart(shift),
    getShiftEnd(shift),
  ]);
  const timeValues = allShiftTimes.filter(isNumber);
  const providedStartHour = toNumber(timelineStartHour);
  const providedEndHour = toNumber(timelineEndHour);
  const startTime = isNumber(providedStartHour)
    ? providedStartHour
    : timeValues.length
    ? Math.min(...timeValues)
    : 0;
  const endTime = isNumber(providedEndHour)
    ? providedEndHour
    : timeValues.length
    ? Math.max(...timeValues)
    : 24;
  const startHour = Math.max(0, Math.floor(startTime || 0));
  const endHour = Math.max(startHour, Math.ceil(endTime || 24));
  const gridOffsetPercent = timelineCompact ? 26 : 15;
  const gridWidthPercent = 100 - gridOffsetPercent;
  const hourIncrement =
    endHour > startHour ? gridWidthPercent / (endHour - startHour) : 0;
  const timelineTickInterval = timelineCompact
    ? endHour - startHour > 16
      ? 3
      : endHour - startHour > 10
      ? 2
      : 1
    : 1;
  const dayIndex = getDayIndex(dateStr, week_start_date);
  const availabilityKey = dayIndex !== null ? `${dayIndex}_availability` : null;

  const getShiftLeftPercent = (shiftStart) => {
    if (!isNumber(shiftStart) || !hourIncrement) {
      return `${gridOffsetPercent}%`;
    }
    return `${gridOffsetPercent + (shiftStart - startHour) * hourIncrement}%`;
  };

  const chartData = showChart
    ? generateLaborSummary(
        dayForecast,
        dayLabor,
        get_t_str,
        "Forecasted",
        "Scheduled",
        true
      )
    : null;

  const gradeScore = calculateScheduleGradeScore(dayLabor, dayForecast, gradeWeights);
  const gradeLetter = calcScheduleLetter(gradeScore);
  const gradeColor = getScheduleGradeColor(gradeScore);

  const forecastHours = sumColumn(dayForecast, "hours");
  const scheduleHours = sumColumn(dayLabor, "hours");

  const renderGridLines = () =>
    Array.from({ length: endHour - startHour + 1 }, (_, i) => (
      <div
        key={`${dateStr}-grid-${i}`}
        className="h-full border-l border-slate-200 absolute z-0"
        style={{ left: `${gridOffsetPercent + hourIncrement * i}%` }}
      />
    ));

  const formatTimelineLabel = (hour) => {
    if (!timelineCompact) {
      return get_t_str(hour);
    }
    const normalizedHour = ((hour % 24) + 24) % 24;
    const hour12 = normalizedHour % 12 || 12;
    return `${hour12}${normalizedHour < 12 ? "a" : "p"}`;
  };

  const renderTimelineLabels = () =>
    Array.from({ length: endHour - startHour + 1 }, (_, i) => {
      const hour = startHour + i;
      const isEndpoint = i === 0 || hour === endHour;
      const shouldShow =
        isEndpoint || (hour - startHour) % timelineTickInterval === 0;
      if (!shouldShow) {
        return null;
      }
      return (
        <div
          key={`${dateStr}-tick-${hour}`}
          className="absolute top-0 text-[11px] text-slate-500"
          style={{
            left: `${gridOffsetPercent + hourIncrement * i}%`,
            transform: i === 0 ? "translateX(0)" : "translateX(-50%)",
          }}
        >
          {formatTimelineLabel(hour)}
        </div>
      );
    });


  return (
    <div
      className={
        embedded
          ? `w-full ${className}`
          : `rounded-2xl border border-slate-200 bg-white p-4 ${
              compact ? "" : "mb-8"
            } ${className}`
      }
    >
      {variantLabel && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">
              {variantLabel}
            </div>
            <div className="text-sm font-semibold text-slate-900">
              {dateStr}
            </div>
          </div>
          <div
            className="flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-2 text-sm font-semibold text-slate-700"
            title={`${rosterCount} ${rosterCount === 1 ? "person" : "people"} on roster`}
          >
            <PeopleAltIcon style={{ fontSize: "17px" }} />
            <span>{rosterCount}</span>
          </div>
        </div>
      )}
      {showTimeline && (
        <div className="w-full">
          {showTimelineAxis && (
            <div className="relative h-7 border-b border-slate-200">
              {renderTimelineLabels()}
            </div>
          )}
          <div className={`w-full relative ${embedded ? "" : "border-b border-slate-200"}`}>
            {showTimelineAxis && renderGridLines()}
            {openShifts
              .sort((a, b) => getShiftStart(a) - getShiftStart(b))
              .map((shift, index) => {
                const roleId = shift.role_id || shift.role_id_id;
                const roleName = getRoleName(roleId, roleNames);
                const roleColorClass = getRoleColorClass(roleId, roleNames);
                const shiftStart = getShiftStart(shift);
                return (
                  <div
                    key={`open-${index}`}
                    className="w-full border-t border-slate-200 py-2 flex items-center relative"
                  >
                    <div
                      className="flex-none pl-2 pr-3"
                      style={{ width: `${gridOffsetPercent}%` }}
                    >
                      <div className="flex min-w-0 items-center gap-1.5">
                        <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-300 text-xs">
                          <span className="text-white">OS</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-slate-900 font-bold text-xxs">
                            Open Shift
                          </p>
                          <p className="text-xxs text-slate-900 whitespace-nowrap">
                            {(shift.hours || 0).toFixed(1)} hrs
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`absolute z-10 flex h-8 min-w-[36px] items-center overflow-hidden whitespace-nowrap rounded-lg px-3 text-xs text-white shadow-sm ${roleColorClass} ${changedShifts.includes(shift) ? "ring-2 ring-violet-500 ring-offset-1" : ""}`}
                      style={{
                        width: `${getShiftDuration(shift) * hourIncrement}%`,
                        left: getShiftLeftPercent(shiftStart),
                      }}
                    >
                      <span className="truncate">
                        {get_t_str(shift.shift_start)} - {get_t_str(shift.shift_end)}
                        {roleName && <span className="ml-2">{roleName}</span>}
                      </span>
                      <ShiftBreakMarkers shift={shift} previousShift={shift.shift_key
                        ? previousSchedule.find((old) => old.shift_key === shift.shift_key) : undefined} />
                    </div>
                  </div>
                );
              })}
            {memberRows.map((member) => (
              <div
                key={member.id}
                className="w-full border-t border-slate-200 py-2 flex items-center relative"
              >
                <div
                  className="flex-none pl-2 pr-3"
                  style={{ width: `${gridOffsetPercent}%` }}
                >
                  <div className="flex min-w-0 items-center gap-1.5">
                    <div
                      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs text-white ${
                        member.primaryRoleId
                          ? getRoleColorClass(member.primaryRoleId, roleNames)
                          : "bg-slate-600"
                      }`}
                    >
                      {buildInitials(member.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-slate-900 font-bold text-xxs">
                        {member.name}
                      </p>
                      <p className="text-xxs text-slate-900 whitespace-nowrap">
                        {member.hours.toFixed(1)} hrs
                      </p>
                    </div>
                  </div>
                </div>
                {getUnavailabilityRanges(
                  availabilityKey
                    ? memberLookup[member.id]?.[availabilityKey]?.[0]
                    : null,
                  startHour,
                  endHour
                ).map((range, index) => (
                  <div
                    key={`${member.id}-unavailable-${index}`}
                    className="bg-slate-300 absolute h-8 rounded-lg z-0"
                    style={{
                      width: `${(range[1] - range[0]) * hourIncrement}%`,
                      left: `${
                        gridOffsetPercent +
                        (range[0] - startHour) * hourIncrement
                      }%`,
                    }}
                  />
                ))}
                {member.shifts.map((shift, index) => {
                  const roleId = shift.role_id || shift.role_id_id;
                  const roleName = getRoleName(roleId, roleNames);
                  const roleColorClass = getRoleColorClass(roleId, roleNames);
                  const shiftStart = getShiftStart(shift);
                  const shiftDuration = getShiftDuration(shift);
                  return (
                    <div
                      key={`${member.id}-${index}`}
                      className={`absolute z-10 flex h-8 min-w-[36px] items-center overflow-hidden whitespace-nowrap rounded-lg px-3 text-xs text-white shadow-sm ${roleColorClass} ${changedShifts.includes(shift) ? "ring-2 ring-violet-500 ring-offset-1" : ""}`}
                      style={{
                        width: `${shiftDuration * hourIncrement}%`,
                        left: getShiftLeftPercent(shiftStart),
                      }}
                    >
                      <span className="truncate">
                        {get_t_str(shift.shift_start)} - {get_t_str(shift.shift_end)}
                        {roleName && <span className="ml-2">{roleName}</span>}
                      </span>
                      <ShiftBreakMarkers shift={shift} previousShift={shift.shift_key
                        ? previousSchedule.find((old) => old.shift_key === shift.shift_key) : undefined} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
      {showChart && (
        <div className={`${showTimeline ? "mt-6" : ""} flex`}>
          <div className="w-[15%] pr-4 flex flex-col gap-3">
            <div className="flex items-center justify-center">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white"
                style={{ backgroundColor: gradeColor }}
              >
                {gradeLetter}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-500">Forecasted</div>
              <div className="text-xs text-slate-500">Hours</div>
              <div className="text-2xl font-bold mt-1">{Math.round(forecastHours || 0)}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-500">Scheduled</div>
              <div className="text-xs text-slate-500">Hours</div>
              <div className="text-2xl font-bold mt-1">{Math.round(scheduleHours || 0)}</div>
            </div>
          </div>
          <div className="w-[85%]">
            <VisualCustomLineChart
              type="daily"
              data={chartData.summary}
              roleTypes={chartData.roleTypes}
              supportNames={chartData.supportNames}
              date_name={dateStr}
              height={264}
              isSmall={true}
              label1="Forecasted"
              label2="Scheduled"
              start_h={startHour}
              end_h={endHour}
              hour_increment={hourIncrement}
              gradeColor={gradeColor}
              gradeLetter={gradeLetter}
              forecastHours={forecastHours}
              scheduleHours={scheduleHours}
              gridColor="#e2e8f0"
            />
          </div>
        </div>
      )}
    </div>
  );
}
